import React, { useRef, useCallback, useEffect } from "react";
import { usePosts } from "../hooks/usePosts";
import ExplorerPost from "./ExplorerPost";


type ExplorerProps = {
  tagNames?: string[];
};

const Explorer: React.FC<ExplorerProps> = ({ tagNames }) => {
  const observerRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = usePosts(tagNames, "regular");

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [handleObserver]);

  // Flatten all posts from all pages and filter out polls
  const allPosts = data?.pages?.flatMap(page => page?.posts || []) || [];
  const regularPosts = allPosts.filter(post => post && post.postType !== 'poll');

  // Generate random heights for Pinterest-style masonry layout
  // Heights range from 200px to 400px for variety
  const getRandomHeight = (index: number) => {
    // Use index as seed for consistent randomness per post
    const seed = index * 7919; // Prime number for better distribution
    const random = Math.sin(seed) * 10000;
    const normalized = random - Math.floor(random);
    return Math.floor(200 + normalized * 200); // Range: 200-400px
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <div className="max-w-6xl mx-auto py-2 px-1">
        {/* Pinterest-style masonry layout with CSS columns */}
        <div 
          className="columns-3 gap-1"
          style={{ columnGap: '4px' }}
        >
          {Array.isArray(regularPosts)
            ? regularPosts
                .filter((post) => post && post.id)
                .map((post, index) => (
                  <ExplorerPost
                    key={post.id}
                    id={post.id}
                    hashCode={post.hashCode}
                    username={post.user?.username || ''}
                    userImage={post.user?.image || ''}
                    img={post.image}
                    video={post.video || null}
                    caption={post.caption}
                    userId={post.userId}
                    postType={post.postType}
                    height={getRandomHeight(index)}
                  />
                ))
            : []}
        </div>
        
        {/* Observer element for infinite scroll */}
        <div ref={observerRef} className="h-4" />
        
        {isFetchingNextPage && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Explorer; 